import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { getNavigationUrlWhileKeepingExistingPermalinks } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { useRouterlessSearchParams } from 'apps/sigma2-ui/src/lib/hooks/useRouterlessSearchParams';

export interface TouchTargetHrefBehavior {
  type: 'href';
  href: string;
}

export interface TouchTargetOnClickBehavior {
  type: 'onClick';
  onClick: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

export type TouchTargetBehavior =
  | TouchTargetHrefBehavior
  | TouchTargetOnClickBehavior;

export const noBehavior: TouchTargetBehavior = {
  type: 'onClick',
  onClick: () => {},
};

export interface TouchTargetProps {
  /**
   * The content of the touch target. You will receive a boolean for each of the following states:
   * - hovered
   * - disabled
   * - focused
   * You should use these to style the content accordingly. You are not necessarily restricted to using these for the style,
   * but it makes it much simpler to conditionally use a different variant/state of the content.
   * @param props
   */
  content: (props: {
    hovered: boolean;
    disabled: boolean;
    focused: boolean;
    active: boolean;
  }) => ReactNode;

  /**
   * If true, the touch target will be disabled. This means that it will not be clickable and will not trigger any hover/focus effects.
   */
  disabled?: boolean;

  /**
   * The class name for the touch target. Ultimately, the touch target usually will not need additional styling,
   * but this may help when your touch target is inside a styled component which is negatively affecting the touch target.
   */
  className?: string;

  /**
   * The behavior of the touch target. This is what will happen when the touch target is clicked.
   * If you want to send the user to a different page, use `href` since it maximizes accessibility.
   * Other than that, both behavior types are designed to allow you to not have to deal with the underlying touch/click event.
   */
  behavior: TouchTargetBehavior;

  /**
   * The role of the touch target. This is used to set the semantic meaning of the element holding the touchable space.
   */
  role?: 'button' | 'link';

  /**
   * An optional test ID for testing purposes.
   */
  testId?: string;

  /**
   * Optional data extras to add to the touch target as data attributes.
   */
  dataExtras?: Record<string, string>;

  label?: string;
}

export const TouchTarget = (props: TouchTargetProps) => {
  const ref = useRef<HTMLAnchorElement>(null);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [active, setActive] = useState(false);
  const searchParams = useRouterlessSearchParams();

  useEffect(() => {
    const onMouseEnter = () => setHovered(true);
    const onMouseLeave = () => setHovered(false);
    const onFocus = () => setFocused(true);
    const onBlur = () => setFocused(false);
    const onMouseDown = () => setActive(true);
    const onMouseUp = () => setActive(false);

    const current = ref.current;
    if (current) {
      current.addEventListener('mouseenter', onMouseEnter);
      current.addEventListener('mouseleave', onMouseLeave);
      current.addEventListener('focus', onFocus);
      current.addEventListener('blur', onBlur);
      current.addEventListener('mousedown', onMouseDown);
      current.addEventListener('mouseup', onMouseUp);
    }
    return () => {
      if (current) {
        current.removeEventListener('mouseenter', onMouseEnter);
        current.removeEventListener('mouseleave', onMouseLeave);
        current.removeEventListener('focus', onFocus);
        current.removeEventListener('blur', onBlur);
        current.removeEventListener('mousedown', onMouseDown);
        current.removeEventListener('mouseup', onMouseUp);
      }
    };
  }, [ref]);

  const targetIsWithinTouchTarget = useCallback((target: EventTarget) => {
    if (!ref.current) {
      return false;
    }

    if (!(target instanceof Node)) {
      return false;
    }

    return ref.current === target || ref.current.contains(target);
  }, []);

  const behaviorProps = useMemo(() => {
    if (props.disabled) {
      return {};
    }

    if (props.behavior.type === 'href') {
      return {
        href: props.behavior.href.startsWith('/')
          ? getNavigationUrlWhileKeepingExistingPermalinks({
              navigateToUrl: props.behavior.href,
              existingSearchParams: searchParams,
            })
          : props.behavior.href,
      };
    }

    const onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (!targetIsWithinTouchTarget(e.target)) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      if (props.behavior.type === 'onClick') {
        props.behavior.onClick(e);
      }
    };

    return { href: '#', onClick };
  }, [props.behavior, props.disabled, targetIsWithinTouchTarget, searchParams]);

  const extraDataAttributes = useMemo(() => {
    const attrs: Record<string, string> = {};
    if (props.dataExtras) {
      for (const [key, value] of Object.entries(props.dataExtras)) {
        attrs[`data-${key}`] = value;
      }
    }
    return attrs;
  }, [props.dataExtras]);

  return (
    <a
      {...behaviorProps}
      aria-disabled={props.disabled ?? false}
      data-is-touchtarget={true}
      data-testid={props.testId === undefined ? undefined : props.testId}
      {...extraDataAttributes}
      className={props.className}
      ref={ref}
      aria-label={props.label}
      title={props.label}
    >
      {props.content({
        hovered: !props.disabled && hovered,
        disabled: props.disabled || false,
        focused: !props.disabled && focused,
        active: !props.disabled && active,
      })}
    </a>
  );
};
